/**
 * Local Audit Log adapters — same central Change History entries, projected
 * into Date and Time · Event · Details · Related Record (or Record).
 */

import {
  formatChangedOnDisplay,
  toLocalAuditLogRow,
  CHANGE_HISTORY_DISPLAY,
} from './changeHistoryEntry';
import {
  getChangeHistoryForSource,
  getLocalAuditLogRows,
  recordChangeHistory,
} from './changeHistoryStore';

function stableSourceChangeRef(parts) {
  return parts.filter(Boolean).join('::');
}

/**
 * Import legacy local audit rows into the central store once (no duplicates).
 */
export function syncLocalAuditLogsToChangeHistory({
  logs = [],
  module,
  sourceModuleRef,
  sourceItemRef,
  accountRef,
  ticketRef,
  caseRef,
  changedItem,
  itemId,
}) {
  (logs || []).forEach((log, index) => {
    const event = log.action || log.event || 'Activity';
    const timestamp =
      log.changedOnIso ||
      log.timestamp ||
      log.dateTime ||
      `seed-${sourceItemRef || accountRef || ticketRef || caseRef}-${index}`;
    const iso =
      timestamp instanceof Date
        ? timestamp.toISOString()
        : typeof timestamp === 'string' && timestamp.includes('T')
          ? timestamp
          : null;
    const sourceChangeRef =
      log.sourceChangeRef ||
      stableSourceChangeRef([
        sourceModuleRef,
        sourceItemRef || accountRef || ticketRef || caseRef,
        event,
        iso || String(timestamp),
        index,
      ]);

    recordChangeHistory({
      sourceChangeRef,
      sourceModuleRef,
      sourceItemRef: sourceItemRef || accountRef || ticketRef || caseRef,
      module,
      changedItem: changedItem || itemId || sourceItemRef || 'Record',
      itemId: itemId || sourceItemRef || CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE,
      changeType: event,
      previousValues: null,
      newValues: null,
      activityOnly: true,
      activityDetails:
        log.details ||
        [log.performedBy, log.summary].filter(Boolean).join(' · ') ||
        CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE,
      changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
      changedBy: log.performedBy || log.changedBy || log.userType || 'System',
      changedOnIso: iso || undefined,
      changedOn:
        iso
          ? formatChangedOnDisplay(iso)
          : typeof timestamp === 'string'
            ? timestamp
            : formatChangedOnDisplay(new Date()),
      relatedRecord: log.relatedRecord || log.record || log.ref?.id || null,
      accountRef: accountRef || null,
      ticketRef: ticketRef || null,
      caseRef: caseRef || null,
      decisionRef: log.decisionRef || null,
    });
  });
}

export function getAccountAuditLogRows(accountRef) {
  if (!accountRef) return [];
  return getLocalAuditLogRows({ accountRef });
}

export function getTicketAuditLogRows(ticketRef) {
  if (!ticketRef) return [];
  return getLocalAuditLogRows({ ticketRef });
}

export function getCaseAuditLogRows(caseRef) {
  if (!caseRef) return [];
  return getLocalAuditLogRows({ caseRef });
}

export function getBillingAuditLogRows(businessRef) {
  if (!businessRef) return [];
  return getLocalAuditLogRows({
    sourceModuleRef: 'billing_subscriptions',
    sourceItemRef: businessRef,
  });
}

/**
 * Record a supported account security / profile activity into central history.
 */
export function recordAccountActivity({
  accountRef,
  accountType = 'Customer',
  changeType,
  activityDetails,
  changedBy = 'System',
  relatedRecord = null,
  itemId,
  sourceChangeRef,
}) {
  const module = accountType === 'Business' ? 'Businesses' : 'Customers';
  const sourceModuleRef = accountType === 'Business' ? 'businesses' : 'customers';
  const iso = new Date().toISOString();
  return recordChangeHistory({
    sourceChangeRef:
      sourceChangeRef ||
      stableSourceChangeRef([sourceModuleRef, accountRef, changeType, iso]),
    sourceModuleRef,
    sourceItemRef: accountRef,
    module,
    changedItem: itemId || accountRef,
    itemId: itemId || accountRef,
    changeType,
    activityOnly: true,
    activityDetails,
    changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy,
    changedOnIso: iso,
    relatedRecord,
    accountRef,
  });
}

export function recordTicketActivity({
  ticketRef,
  changeType,
  activityDetails,
  changedBy = 'System',
  relatedRecord = null,
  sourceChangeRef,
}) {
  const iso = new Date().toISOString();
  return recordChangeHistory({
    sourceChangeRef:
      sourceChangeRef || stableSourceChangeRef(['support_tickets', ticketRef, changeType, iso]),
    sourceModuleRef: 'support_tickets',
    sourceItemRef: ticketRef,
    module: 'Support Tickets',
    changedItem: ticketRef,
    itemId: ticketRef,
    changeType,
    activityOnly: true,
    activityDetails,
    changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy,
    changedOnIso: iso,
    relatedRecord: relatedRecord || ticketRef,
    ticketRef,
  });
}

export function recordCaseActivity({
  caseRef,
  module = 'Fraud & Security',
  sourceModuleRef = 'fraud_security',
  changeType,
  activityDetails,
  changedBy = 'System',
  relatedRecord = null,
  decisionRef = null,
  sourceChangeRef,
  changedOnIso,
}) {
  const iso = changedOnIso || new Date().toISOString();
  return recordChangeHistory({
    sourceChangeRef:
      sourceChangeRef || stableSourceChangeRef([sourceModuleRef, caseRef, changeType, iso]),
    sourceModuleRef,
    sourceItemRef: caseRef,
    module,
    changedItem: caseRef,
    itemId: caseRef,
    changeType,
    activityOnly: true,
    activityDetails,
    changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy,
    changedOnIso: iso,
    relatedRecord: relatedRecord || decisionRef || caseRef,
    caseRef,
    decisionRef,
  });
}

/**
 * Billing & Subscriptions operational activity (notes, cancellations, etc.).
 */
export function recordBillingActivity({
  businessRef,
  changeType,
  activityDetails,
  changedBy = 'System',
  relatedRecord = null,
  itemId,
  sourceChangeRef,
}) {
  const iso = new Date().toISOString();
  const ref = businessRef || itemId;
  if (!ref) return null;
  return recordChangeHistory({
    sourceChangeRef:
      sourceChangeRef ||
      stableSourceChangeRef(['billing_subscriptions', ref, changeType, iso]),
    sourceModuleRef: 'billing_subscriptions',
    sourceItemRef: ref,
    module: 'Billing & Subscriptions',
    changedItem: itemId || ref,
    itemId: itemId || ref,
    changeType,
    activityOnly: true,
    activityDetails,
    changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy,
    changedOnIso: iso,
    relatedRecord: relatedRecord || ref,
    accountRef: businessRef || null,
  });
}

/**
 * Reviews operational activity (notes, visibility changes via admin screens).
 */
export function recordReviewActivity({
  reviewRef,
  changeType,
  activityDetails,
  changedBy = 'System',
  relatedRecord = null,
  sourceChangeRef,
}) {
  const iso = new Date().toISOString();
  if (!reviewRef) return null;
  return recordChangeHistory({
    sourceChangeRef:
      sourceChangeRef ||
      stableSourceChangeRef(['reviews_management', reviewRef, changeType, iso]),
    sourceModuleRef: 'reviews_management',
    sourceItemRef: reviewRef,
    module: 'Reviews',
    changedItem: reviewRef,
    itemId: reviewRef,
    changeType,
    activityOnly: true,
    activityDetails,
    changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy,
    changedOnIso: iso,
    relatedRecord: relatedRecord || reviewRef,
  });
}

/**
 * Messages / Quotes thread activity for Screen 2 Audit Log.
 */
export function recordThreadActivity({
  threadRef,
  isQuote = false,
  changeType,
  activityDetails,
  changedBy = 'System',
  relatedRecord = null,
  sourceChangeRef,
}) {
  const iso = new Date().toISOString();
  if (!threadRef) return null;
  const module = isQuote ? 'Quotes' : 'Messages';
  const sourceModuleRef = isQuote ? 'quotes' : 'messages';
  return recordChangeHistory({
    sourceChangeRef:
      sourceChangeRef ||
      stableSourceChangeRef([sourceModuleRef, threadRef, changeType, iso]),
    sourceModuleRef,
    sourceItemRef: threadRef,
    module,
    changedItem: threadRef,
    itemId: threadRef,
    changeType,
    activityOnly: true,
    activityDetails,
    changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy,
    changedOnIso: iso,
    relatedRecord: relatedRecord || threadRef,
  });
}

export function getThreadAuditLogRows(threadRef, { isQuote = false } = {}) {
  if (!threadRef) return [];
  const sourceModuleRef = isQuote ? 'quotes' : 'messages';
  return getLocalAuditLogRows({ sourceModuleRef, sourceItemRef: threadRef });
}

export function hasCentralHistoryForSource(scope) {
  return getChangeHistoryForSource(scope).length > 0;
}

export { toLocalAuditLogRow, getLocalAuditLogRows };
